"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Shuffle,
  X,
} from "lucide-react";
import { QuizCard, QuestionData } from "./QuizCard";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface QuizViewProps {
  questions: QuestionData[];
  className?: string;
}

export function QuizView({ questions, className }: QuizViewProps) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(() =>
    Array(questions.length).fill(null)
  );

  const currentCard = questions[index];
  const total = questions.length;

  /* ---------- derived ---------- */

  const answeredCount = useMemo(
    () => answers.filter((a) => a !== null).length,
    [answers]
  );

  const correctCount = useMemo(
    () =>
      answers.reduce((acc, answer, i) => {
        if (answer === questions[i].correctAnswer) acc++;
        return acc;
      }, 0),
    [answers, questions]
  );

  const progressPct = useMemo(() => {
    if (total === 0) return 0;
    return ((index + 1) / total) * 100;
  }, [index, total]);

  const results = useMemo(() => {
    return answers.map((answer, i) => {
      if (answer === null) return "unanswered";
      return answer === questions[i].correctAnswer ? "correct" : "incorrect";
    });
  }, [answers, questions]);

  /* ---------- actions ---------- */

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Ignore key presses while typing in inputs (future-proofing)
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setIndex((i) => Math.max(0, i - 1));
      }

      if (e.key === "ArrowRight") {
        e.preventDefault();
        setIndex((i) => Math.min(total - 1, i + 1));
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [total]);

  const handleAnswer = (chosen: number) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = chosen;
      return next;
    });
  };

  const resetQuiz = () => {
    setAnswers(Array(total).fill(null));
    setIndex(0);
  };

  const textAction =
    "inline-flex items-center gap-1 text-xs transition " +
    "text-theme-1 hover:scale-110 active:scale-90";
  if (!currentCard) return null;

  return (
    <div
      className={cn(
        "w-full max-w-xl mx-auto flex flex-col items-center",
        className
      )}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between w-full text-xs text-theme-1">
        <span>
          Question {index + 1} of {total}
        </span>
      </div>

      {/* Card + ambient blur */}
      <div className="relative w-full flex justify-center">
        {/* Left blur */}
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute left-[-50px] top-1/2 -translate-y-1/2",
            "h-64 w-64 rounded-full blur-2xl opacity-20"
          )}
          style={{
            background: "radial-gradient(circle, rgba(99,102,241,1))",
          }}
        />

        {/* Right blur */}
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute right-[-50px] top-1/2 -translate-y-1/2",
            "h-64 w-64 rounded-full blur-2xl opacity-20"
          )}
          style={{
            background: "radial-gradient(circle, oklch(70.5% 0.213 47.604))",
          }}
        />

        {/* Card */}
        <div className="relative z-10 mb-5">
          <QuizCard
            key={index}
            card={currentCard}
            value={answers[index]}
            onAnswer={handleAnswer}
          />
        </div>
      </div>

      {/* Navigation */}
      <div className="mt-4 flex items-center justify-between w-full">
        <button
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={index === 0}
          className="flex items-center gap-1 px-3 py-2 rounded-lg text-theme-0 transition
                     hover:bg-theme-1 active:scale-[0.97]
                     disabled:opacity-40 disabled:pointer-events-none"
        >
          <ChevronLeft className="h-4 w-4" />
          Prev
        </button>

        <div className="text-xs text-theme-1">
          {index + 1} / {total}
        </div>

        <button
          onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
          disabled={index === total - 1}
          className="flex items-center gap-1 px-3 py-2 rounded-lg text-theme-0 transition
                     hover:bg-theme-1 active:scale-[0.97]
                     disabled:opacity-40 disabled:pointer-events-none"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Progress bar */}
      {/* Segmented progress bar */}
      <div className="mt-4 w-full">
        {/* Bar */}
        <div className="flex h-2 w-full gap-1">
          {results.map((result, i) => {
            const isCurrent = i === index;

            return (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={cn(
                  "relative flex-1 rounded-full transition-all cursor-pointer",
                  "hover:brightness-105",
                  result === "correct" && "bg-emerald-400",
                  result === "incorrect" && "bg-red-400",
                  result === "unanswered" && "bg-theme-2",
                  isCurrent && "ring-2 ring-offset-2 ring-gray-400",
                  isCurrent && result === "correct" && "ring-emerald-400",
                  isCurrent && result === "incorrect" && "ring-red-400"
                )}
                aria-label={`Go to question ${i + 1}`}
              />
            );
          })}
        </div>

        {/* Icons */}
        <div className="mt-2 flex w-full gap-1">
          {results.map((result, i) => (
            <div
              key={i}
              className="flex-1 flex justify-center transition-opacity duration-300"
            >
              {result === "correct" && (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              )}
              {result === "incorrect" && (
                <X className="h-3.5 w-3.5 text-red-500" />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between w-full">
        <div className="flex-1" />
        <div className="flex gap-4">
          <button onClick={resetQuiz} className={textAction}>
            <RotateCcw className="h-3.5 w-3.5" />
            Restart
          </button>
        </div>
      </div>
    </div>
  );
}
